import { type RootError, useIntl, type UserRequirement } from '@capsicum/common';
import {
	BaseSelectInput,
	Button,
	DateInput,
	FormProvider,
	SelectInput,
	Text,
	TextInput,
	useInfoModal
} from '@capsicum/common-web';
import { ChevronDownIcon } from '@capsicum/common-web/icons';
import {
	ITALIAN_COUNTRY,
	type ItalianOAMCountryProps,
	type ItalianOAMMunicipalityProps,
	type ItalianOAMProvinceProps,
	useItalianOam
} from '@capsicum/features/regulations/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

type ItalianOAMProps = {
	isForcedRequirement?: boolean;
	onDismiss: (callback?: () => void) => void;
	requirement: UserRequirement;
};

export const ItalianOam = ({ isForcedRequirement, onDismiss, requirement }: ItalianOAMProps) => {
	const { $t } = useIntl();
	const {
		addressCountry,
		addressProvince,
		birthCountry,
		canUpdatePersonalData,
		defaultProvince,
		filterMunicipalitiesByProvince,
		handleSubmitForm,
		isLoading,
		italianOamForm,
		provinces,
		resetBirthInformation,
		resetResidenceInformation,
		resetResidenceMunicipality,
		selectBirthProvince,
		selectedBirthProvince,
		submitRequirementError,
		translatedCountries
	} = useItalianOam({
		requirement
	});
	const [rootError, setRootError] = useState({} as RootError);
	const [showModal, setShowModal] = useState(true);
	const onModalConfirmPress = () => setShowModal(false);

	const { showInfoModal } = useInfoModal();

	const itemTemplate: (params: { item: ItalianOAMMunicipalityProps | ItalianOAMProvinceProps }) => string =
		useCallback(params => {
			return params.item.name as string;
		}, []);

	const onSearch = <T extends { name?: string }>(data: Array<T>, text: string) =>
		data.filter(item => item?.name?.toLowerCase().includes(text.toLowerCase()));

	const renderBirthMunicipalityInput = useMemo(() => {
		if (birthCountry?.name === ITALIAN_COUNTRY) {
			return (
				<>
					<BaseSelectInput
						data={provinces as Array<ItalianOAMProvinceProps>}
						Icon={ChevronDownIcon}
						itemTemplate={itemTemplate}
						label={$t('jurisdictionRequirements.italianOAM.form.provinceOfBirth.label')}
						labelProp="name"
						testID="italian-oam-birth-province-input"
						value={selectedBirthProvince}
						withSearch
						onChange={selectBirthProvince}
						onSearch={onSearch}
					/>
					<SelectInput
						data={
							filterMunicipalitiesByProvince(
								selectedBirthProvince?.name || ''
							) as Array<ItalianOAMMunicipalityProps>
						}
						disabled={!selectedBirthProvince}
						Icon={ChevronDownIcon}
						itemTemplate={itemTemplate}
						label={$t('jurisdictionRequirements.italianOAM.form.municipalityOfBirth.label')}
						labelProp="name"
						name="birthMunicipality"
						testID="italian-oam-birth-municipality-input"
						withSearch
						onSearch={onSearch}
					/>
				</>
			);
		} else {
			return (
				<TextInput
					label={$t('jurisdictionRequirements.italianOAM.form.municipalityOfBirth.label')}
					name="birthMunicipality"
					testID="italian-oam-birth-municipality-input"
				/>
			);
		}
	}, [provinces, selectBirthProvince, selectedBirthProvince]);

	const renderResidenceMunicipalityInput = useMemo(() => {
		if (addressCountry.name === ITALIAN_COUNTRY) {
			return (
				<>
					<SelectInput
						data={provinces as Array<ItalianOAMProvinceProps>}
						disabled={addressCountry.name !== ITALIAN_COUNTRY}
						Icon={ChevronDownIcon}
						itemTemplate={itemTemplate}
						label={$t('jurisdictionRequirements.italianOAM.form.residenceProvince.label')}
						labelProp="name"
						name="addressProvince"
						testID="italian-oam-residence-province-input"
						withSearch
						onSearch={onSearch}
						onValueChange={resetResidenceMunicipality}
					/>
					<SelectInput
						data={
							filterMunicipalitiesByProvince(
								defaultProvince as string
							) as Array<ItalianOAMMunicipalityProps>
						}
						disabled={addressCountry.name !== ITALIAN_COUNTRY || !addressProvince}
						Icon={ChevronDownIcon}
						itemTemplate={itemTemplate}
						label={$t('jurisdictionRequirements.italianOAM.form.residenceMunicipality.label')}
						labelProp="name"
						name="addressMunicipality"
						testID="italian-oam-residence-municipality-input"
						withSearch
						onSearch={onSearch}
					/>
				</>
			);
		} else {
			return (
				<TextInput
					label={$t('jurisdictionRequirements.italianOAM.form.residenceMunicipality.label')}
					name="addressMunicipality"
					testID="italian-oam-residence-municipality-input"
				/>
			);
		}
	}, [addressCountry, addressProvince, provinces]);

	useEffect(
		() =>
			showInfoModal({
				buttonOnHandlers: {
					onPrimaryButtonHandler: onModalConfirmPress,
					primaryInvokeOnDismiss: false
				},
				buttonTexts: {
					primaryButtonText: $t('requirements.common.modal.button')
				},
				image: 'questionnaire',
				isClosable: !isForcedRequirement,
				onDismiss,
				subtitle: $t('requirements.common.modal.description'),
				testID: 'italian-oam-modal',
				title: $t('requirements.common.modal.title'),
				variant: 'confirm'
			}),
		[]
	);

	if (showModal) return <S.EmptyContainer />;

	return (
		<S.Wrapper>
			<FormProvider
				error={submitRequirementError}
				form={italianOamForm}
				testID="italian-oam"
				onRootError={errors => setRootError(errors)}
			>
				<S.BlockContainer>
					<S.BlockTitle>{$t('jurisdictionRequirements.italianOAM.personalInformation')}</S.BlockTitle>
					<S.InputsContainer>
						<TextInput
							disabled={!canUpdatePersonalData}
							label={$t('global.firstName')}
							name="firstName"
							testID="italian-oam-first-name-input"
						/>
						<TextInput
							disabled={!canUpdatePersonalData}
							label={$t('global.lastName')}
							name="lastName"
							testID="italian-oam-last-name-input"
						/>
						<TextInput
							label={$t('jurisdictionRequirements.italianOAM.form.codiceFiscale.label')}
							name="codiceFiscale"
							testID="italian-oam-codice-fiscale-input"
						/>
						<DateInput
							disabled={!canUpdatePersonalData}
							label={$t('requirements.common.form.dateOfBirth.label')}
							name="dateOfBirth"
							testID="italian-oam-date-of-birth-input"
						/>
						<SelectInput
							data={translatedCountries as Array<ItalianOAMCountryProps>}
							Icon={ChevronDownIcon}
							itemTemplate={itemTemplate}
							label={$t('jurisdictionRequirements.italianOAM.form.countryOfBirth.label')}
							labelProp="name"
							name="birthCountry"
							testID="italian-oam-country-of-birth-input"
							withSearch
							onSearch={onSearch}
							onValueChange={resetBirthInformation}
						/>
						{renderBirthMunicipalityInput}
					</S.InputsContainer>
				</S.BlockContainer>
				<S.BlockContainer>
					<S.BlockTitle>{$t('jurisdictionRequirements.italianOAM.addressInformation')}</S.BlockTitle>
					<S.InputsContainer>
						<SelectInput
							data={translatedCountries as Array<ItalianOAMCountryProps>}
							Icon={ChevronDownIcon}
							itemTemplate={itemTemplate}
							label={$t('jurisdictionRequirements.italianOAM.form.residenceCountry.label')}
							labelProp="name"
							name="addressCountry"
							testID="italian-oam-residence-country-input"
							withSearch
							onSearch={onSearch}
							onValueChange={resetResidenceInformation}
						/>
						{renderResidenceMunicipalityInput}
						<TextInput
							label={$t('jurisdictionRequirements.italianOAM.form.residenceAddress.label')}
							name="addressStreet"
							testID="italian-oam-residence-address-input"
						/>

						{rootError?.message && (
							<Text color="error.enabled" data-testid="italian-oam-root-error-message" variant="text3">
								{rootError.message}
							</Text>
						)}
						<S.ButtonsContainer>
							<Button
								disabled={!italianOamForm.formState.isValid}
								fullWidth
								isLoading={isLoading}
								testID="italian-oam-fields-confirm-button"
								text={$t('global.button.submit')}
								onClick={handleSubmitForm}
							/>
							<Button
								color="neutral"
								fullWidth
								testID="italian-oam-fields-cancel-button"
								text={$t('global.button.cancel')}
								variant="text"
								onClick={() => onDismiss()}
							/>
						</S.ButtonsContainer>
					</S.InputsContainer>
				</S.BlockContainer>
			</FormProvider>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0 51px`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small} 51px`};
		}
	`;

	export const BlockContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
		margin-bottom: ${({ theme }) => theme.spacings.medium};
	`;

	export const BlockTitle = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
	`;

	export const InputsContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
		padding: ${({ theme }) => `${theme.spacings.large} ${theme.spacings.small}`};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
	`;

	export const ButtonsContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const EmptyContainer = styled.div`
		flex: 1;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;
}
